/**
 * migrateProgress
 *
 * Pure function. Converts stored user progress (any format) into the new
 * flat progressById format used by the lesson engine.
 *
 * No React. No side effects. Safe to call at startup before any rendering.
 *
 * Handles three input cases:
 *   "empty"   — user has no stored progress (new user)
 *   "old"     — stored as nested tree: { courses: [{ lessons: [{ steps: [...] }] }] }
 *   "new"     — already flat: { progressById: { "id": { completed: bool }, ... }, activeStepItemId: "..." }
 *
 * After format detection and conversion, runs legacy ID resolution so that
 * users who completed steps that were later renamed or split don't lose progress.
 */

import { repairActiveStepItemId } from './buildRecipeIndex';


// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------

function detectProgressFormat(stored) {
  if (!stored || Object.keys(stored).length === 0) return 'empty';
  if (stored.courses) return 'old';
  if (stored.progressById) return 'new';
  return 'unknown';
}


// ---------------------------------------------------------------------------
// Flatten old nested format → flat map
// ---------------------------------------------------------------------------

function flattenOldProgress(oldProgress) {
  const flat = {};

  (oldProgress.courses || []).forEach((course) => {
    if (course.id) flat[course.id] = { completed: !!course.completed };

    (course.lessons || []).forEach((lesson) => {
      if (lesson.id) flat[lesson.id] = { completed: !!lesson.completed };

      (lesson.steps || []).forEach((step) => {
        if (step.id) flat[step.id] = { completed: !!step.completed };

        (step.stepItems || []).forEach((stepItem) => {
          if (stepItem.id) {
            // Use OR: if ANY occurrence of this ID was completed, keep it completed.
            // Handles duplicate step IDs in old recipes (e.g. puppet_glue-on-stick).
            const prev = flat[stepItem.id];
            flat[stepItem.id] = { completed: !!(prev?.completed || stepItem.completed) };
          }
        });
      });
    });
  });

  return flat;
}


// ---------------------------------------------------------------------------
// Legacy ID resolution
// ---------------------------------------------------------------------------

/**
 * For each stepItem in the current recipe, checks if it has a stored progress
 * record. If not, checks its legacyIds to inherit completion from a renamed
 * or split predecessor.
 *
 * legacyMode "anyOf" (default, used for splits):
 *   Completed if ANY legacy ID was completed.
 *
 * legacyMode "allOf" (used for merges):
 *   Completed only if ALL legacy IDs were completed.
 */
function resolveLegacyIds(idx, flatStored) {
  const out = {};

  for (const stepItemId of idx.stepItemIds) {
    const direct = flatStored[stepItemId];
    if (direct !== undefined) {
      out[stepItemId] = direct;
      continue;
    }

    const legacyIds = idx.legacyIdsOf[stepItemId] || [];
    const mode = idx.legacyModeOf[stepItemId] || 'anyOf';

    if (legacyIds.length === 0) {
      out[stepItemId] = { completed: false };
      continue;
    }

    const legacyRecords = legacyIds.map((oldId) => flatStored[oldId]).filter(Boolean);

    if (mode === 'allOf') {
      const allDone = legacyRecords.length === legacyIds.length &&
                      legacyRecords.every((r) => r.completed);
      out[stepItemId] = { completed: allDone };
    } else {
      // anyOf (default)
      const found = legacyRecords.find((r) => r.completed);
      out[stepItemId] = found ? { ...found } : { completed: false };
    }
  }

  return out;
}


// ---------------------------------------------------------------------------
// Main migration function
// ---------------------------------------------------------------------------

/**
 * @param {object} idx          - RecipeIndex from buildRecipeIndex()
 * @param {object} stored       - Raw stored value from DB (any format)
 * @param {object} [options]
 * @param {boolean} [options.dryRun] - If true, logs result but does not return it
 *                                     (useful for pre-flight verification)
 * @returns {{ progressById: object, activeStepItemId: string }}
 */
function migrateProgress(idx, stored, options = {}) {
  const { dryRun = false } = options;
  const format = detectProgressFormat(stored);

  let flatStored = {};
  let storedActiveStepItemId = null;

  if (format === 'empty') {
    // New user — nothing to migrate
    flatStored = {};
  } else if (format === 'old') {
    // Convert nested tree to flat map
    flatStored = flattenOldProgress(stored);
  } else if (format === 'new') {
    // Already flat — use directly
    flatStored = stored.progressById || {};
    storedActiveStepItemId = stored.activeStepItemId || null;
  } else {
    console.warn('[migrateProgress] Unknown stored format. Starting fresh.', stored);
    flatStored = {};
  }

  // Resolve legacyIds for all stepItems in current recipe
  const progressById = resolveLegacyIds(idx, flatStored);

  // Repair the activeStepItemId if it no longer exists in recipe
  const repairedActiveStepItemId = storedActiveStepItemId
    ? repairActiveStepItemId(idx, progressById, storedActiveStepItemId)
    : deriveActiveStepItemId(idx, progressById);

  // If the cursor sits on an already-completed item, advance it.
  // This happens when CELEBRATION_DONE moves the cursor in memory but the
  // update never reaches the DB (saveRevision is not incremented by that action).
  // On next load, the stored cursor still points to the last item of the finished
  // lesson, making isCursorInCurrentLesson false for the next lesson.
  const finalActiveStepItemId = progressById[repairedActiveStepItemId]?.completed
    ? deriveActiveStepItemId(idx, progressById)
    : repairedActiveStepItemId;

  const result = {
    progressById,
    activeStepItemId: finalActiveStepItemId,
    migratedFrom: format,
  };

  if (dryRun) {
    console.group('[migrateProgress] DRY RUN — not saving');
    console.log('Input format:', format);
    console.log('progressById:', progressById);
    console.log('activeStepItemId:', finalActiveStepItemId);
    console.groupEnd();
    return result;
  }

  return result;
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derives a sensible activeStepItemId from progressById when none is stored.
 *
 * Strategy: cursor = item right after the last completed item.
 * This naturally handles all cases:
 *   - Fresh user (nothing completed)        → item 0
 *   - Mid-step (some items done)            → next item in the step
 *   - Between steps (last step just done)   → first item of next step (even if skippable)
 *   - Everything done                       → sit at last item
 */
function deriveActiveStepItemId(idx, progressById) {
  let lastCompletedPos = -1;
  for (let i = 0; i < idx.stepItemIds.length; i++) {
    if (progressById[idx.stepItemIds[i]]?.completed) {
      lastCompletedPos = i;
    }
  }

  if (lastCompletedPos === -1) {
    // No completions at all — fresh start
    return idx.stepItemIds[0] ?? null;
  }

  if (lastCompletedPos >= idx.stepItemIds.length - 1) {
    // Last item is completed — sit at the end
    return idx.stepItemIds[idx.stepItemIds.length - 1] ?? null;
  }

  // Advance to the item right after the last completed one
  return idx.stepItemIds[lastCompletedPos + 1];
}


export {
  migrateProgress,
  detectProgressFormat,
  flattenOldProgress,
  deriveActiveStepItemId,
};
